<script setup>
/**
 * What came with a task drafted from a description (spec P-8.2), on the Check screen:
 *
 *  - the steps that need a system she has not connected ("Needs a connected system"), which hand
 *    the chat to her team at that point until she connects one;
 *  - anything Waslo could not finish, named, for her to fix before she turns the task on;
 *  - "Also add it to my menu", PRE-TICKED, acted on only when she turns the task on: a menu option
 *    that starts a switched-off task would show customers a choice that does nothing;
 *  - an approved message to go with it, which opens the message editor with the goal written in
 *    and never sends anything.
 */
import { computed, onMounted, ref } from 'vue';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import WasloSelect from '../WasloSelect.vue';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { useWasloI18n } from '../../i18n.js';
import { describedProblemMessage } from '../../helpers/setup-ai.js';
import { templatesApi } from '../../api/templates.js';

const props = defineProps({
  /** `{ notes, problems, menuOption, approvedMessage }` from `wasloApi.describeTask`. */
  described: { type: Object, required: true },
  /** The account's menus (`wasloApi.getMenus`), or `null` while not known. */
  menus: { type: Array, default: null },
  /** `{ add: boolean, menuId: number|null }`. */
  menuChoice: { type: Object, required: true },
});

const emit = defineEmits(['update:menuChoice']);

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();

const needsSystem = computed(() =>
  props.described.notes.filter(note => note.kind === 'needs_system')
);
const leftOut = computed(() =>
  props.described.notes.filter(note => note.kind === 'left_out')
);

/** Menus with room for one more option (ten at most). */
const roomy = computed(() =>
  (props.menus ?? []).filter(menu => menu.optionCount < 10)
);
const menuOptions = computed(() =>
  roomy.value.map(menu => ({ value: String(menu.id), label: menu.title }))
);
const optionWords = computed(() => {
  const option = props.described.menuOption;
  if (!option) return '';
  return String(locale.value ?? '').startsWith('ar')
    ? option.titleAr || option.titleEn
    : option.titleEn || option.titleAr;
});

const setAdd = add => emit('update:menuChoice', { ...props.menuChoice, add });
const setMenu = value =>
  emit('update:menuChoice', {
    ...props.menuChoice,
    menuId: Number(value) || null,
  });

/** An approved message is written for one WhatsApp number: the first one, as the Templates page opens. */
const senderId = ref(null);
onMounted(async () => {
  if (!props.described.approvedMessage || accountId.value === null) return;
  try {
    const senders = await templatesApi.senders(accountId.value);
    senderId.value = senders[0]?.id ?? null;
  } catch {
    senderId.value = null;
  }
});

const messageLink = computed(() => {
  const goal = props.described.approvedMessage?.goal;
  if (!goal || accountId.value === null || senderId.value === null) return null;
  return {
    path: frontendURL(`accounts/${accountId.value}/waslo/templates/new`),
    query: {
      inboxId: String(senderId.value),
      goal: 'custom',
      describe: goal.slice(0, 300),
    },
  };
});
</script>

<template>
  <section class="flex flex-col gap-3" data-waslo-described-notes>
    <p
      v-for="note in needsSystem"
      :key="note.stepId"
      class="mb-0 text-sm text-n-amber-11"
      data-waslo-described-needs-system
    >
      <bdi>{{
        t('WASLO.SETUP_AI.DESCRIBE.NEEDS_SYSTEM', { what: note.detail })
      }}</bdi>
    </p>
    <p
      v-for="note in leftOut"
      :key="`left-${note.stepId}`"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-described-left-out
    >
      {{
        t('WASLO.SETUP_AI.DESCRIBE.LEFT_OUT', { count: Number(note.detail) })
      }}
    </p>
    <div
      v-if="described.problems.length"
      class="flex flex-col gap-1"
      data-waslo-described-problems
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.SETUP_AI.DESCRIBE.PROBLEMS_HEADING') }}
      </p>
      <p
        v-for="(problem, index) in described.problems"
        :key="index"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ describedProblemMessage(problem, t) }}
      </p>
    </div>
    <div
      v-if="described.menuOption && roomy.length"
      class="flex flex-col gap-2"
      data-waslo-described-menu
    >
      <label
        class="flex items-start gap-3 text-sm cursor-pointer text-n-slate-12"
      >
        <span class="mt-0.5 shrink-0">
          <Checkbox
            :model-value="menuChoice.add"
            data-waslo-described-menu-add
            @update:model-value="setAdd"
          />
        </span>
        <span>
          <bdi>{{
            t('WASLO.SETUP_AI.DESCRIBE.MENU_ADD', { option: optionWords })
          }}</bdi>
        </span>
      </label>
      <WasloSelect
        v-if="menuChoice.add && roomy.length > 1"
        :model-value="
          menuChoice.menuId === null ? '' : String(menuChoice.menuId)
        "
        :label="t('WASLO.SETUP_AI.DESCRIBE.MENU_WHICH')"
        :options="menuOptions"
        data-waslo-described-menu-which
        @update:model-value="setMenu"
      />
    </div>
    <router-link
      v-if="messageLink"
      :to="messageLink"
      class="text-sm underline text-n-slate-12"
      data-waslo-described-message
    >
      {{ t('WASLO.SETUP_AI.DESCRIBE.MESSAGE') }}
    </router-link>
  </section>
</template>
